import { useCallback, useMemo, useSyncExternalStore } from "react";

/**
 * Persistuje "sticky" subset filterów strateg-cenowy w `localStorage` per-user.
 * User wraca jutro i dalej widzi swoje wybory NRF/OTC/RX + kategoria biznesowa
 * + akcja cenowa, bez resetu. Pola ad-hoc (`search`, `tagId`, `showExcluded`)
 * NIE są persistowane celowo — user nie powinien wracać do wczorajszego texta
 * wyszukiwarki ani do edge-case flagi widoczności wykluczonych.
 *
 * Key zawiera userId — dwóch userów na wspólnym kompie nie miesza filterów.
 * Brak userId (np. krótka chwila przed hydracją auth) → key `anon`.
 *
 * Storage event + custom event w celu sync między zakładkami i wewnątrz strony.
 */

const CHANGE_EVENT = "strateg-filters:changed";

export interface StrategPersistedFilters {
	categories: string[];
	primaryCategoryCode: string | null;
	priceAction: string | null;
	/** Filtr mechanizmu cenowego (Phase B backend) — `"bez_straty"` lub `null`. */
	mechanism: string | null;
}

export const EMPTY_PERSISTED_FILTERS: StrategPersistedFilters = {
	categories: [],
	primaryCategoryCode: null,
	priceAction: null,
	mechanism: null,
};

function subscribe(callback: () => void): () => void {
	const onStorage = () => callback();
	const onCustom = () => callback();
	window.addEventListener("storage", onStorage);
	window.addEventListener(CHANGE_EVENT, onCustom);
	return () => {
		window.removeEventListener("storage", onStorage);
		window.removeEventListener(CHANGE_EVENT, onCustom);
	};
}

function readSnapshot(key: string): string | null {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

function parseSubset(raw: string | null): StrategPersistedFilters {
	if (!raw) return EMPTY_PERSISTED_FILTERS;
	try {
		const parsed = JSON.parse(raw) as Record<string, unknown>;
		const categories = Array.isArray(parsed.categories)
			? parsed.categories.filter((c): c is string => typeof c === "string")
			: [];
		// Rodzic "30 ATRYBUT" zapisany przed poprawka pickera — backend odrzuca go 400.
		const primaryCategoryCode =
			typeof parsed.primaryCategoryCode === "string" && parsed.primaryCategoryCode !== "30"
				? parsed.primaryCategoryCode
				: null;
		const priceAction = typeof parsed.priceAction === "string" ? parsed.priceAction : null;
		const mechanism = typeof parsed.mechanism === "string" ? parsed.mechanism : null;
		return { categories, primaryCategoryCode, priceAction, mechanism };
	} catch {
		return EMPTY_PERSISTED_FILTERS;
	}
}

/**
 * Pure read persisted filtrow poza Reactem — dla route loadera (prefetch
 * pierwszej strony wynikow). MUSI czytac ten sam klucz i parsowac tak samo
 * jak hook ponizej, inaczej queryKey prefetchu rozjedzie sie z mountem strony.
 */
export function readPersistedFilters(userId: number | null | undefined): StrategPersistedFilters {
	if (typeof window === "undefined") return EMPTY_PERSISTED_FILTERS;
	return parseSubset(readSnapshot(`strateg-cenowy:filters:${userId ?? "anon"}`));
}

export function useStrategFiltersPersistedState(
	userId: number | null | undefined,
): [StrategPersistedFilters, (next: StrategPersistedFilters) => void] {
	const key = `strateg-cenowy:filters:${userId ?? "anon"}`;
	// useSyncExternalStore wymaga stabilnej referencji snapshot — trzymamy raw
	// string i parse'ujemy w useMemo (deps: raw), inaczej infinite re-render.
	const raw = useSyncExternalStore(
		subscribe,
		() => readSnapshot(key),
		() => null,
	);
	const value = useMemo(() => parseSubset(raw), [raw]);

	const setValue = useCallback(
		(next: StrategPersistedFilters) => {
			try {
				window.localStorage.setItem(key, JSON.stringify(next));
			} catch {}
			window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
		},
		[key],
	);

	return [value, setValue];
}
